/* ==========================================================================
   Lalla — recommendations.js
   <product-recommendations>  loads Shopify product recommendations.
   <recently-viewed>          renders recently viewed products.
   ========================================================================== */

(() => {
  const RECENT_KEY = 'lalla:recently-viewed';

  if (!customElements.get('product-recommendations')) {
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        if (this.ready || !this.dataset.url) return;
        this.ready = true;
        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        // Load only when the section approaches the viewport.
        new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        ).observe(this);
      }

      async load() {
        try {
          const response = await fetch(this.dataset.url);
          if (!response.ok) throw new Error(response.status);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (fresh && fresh.innerHTML.trim()) {
            this.innerHTML = fresh.innerHTML;
            if (window.Lalla) window.Lalla.initReveal(this);
          } else {
            this.closest('.shopify-section')?.classList.add('is-empty');
            this.hidden = true;
          }
        } catch (error) {
          this.hidden = true;
        }
      }
    }
    customElements.define('product-recommendations', ProductRecommendations);
  }

  if (!customElements.get('recently-viewed')) {
    class RecentlyViewed extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;
        let handles = [];
        try {
          handles = JSON.parse(window.localStorage.getItem(RECENT_KEY) || '[]');
        } catch (error) {
          return;
        }
        const limit = parseInt(this.dataset.limit, 10) || 4;
        this.handles = handles.filter((h) => h && h !== this.dataset.exclude).slice(0, limit);
        if (!this.handles.length) return;

        new IntersectionObserver(
          (entries, observer) => {
            if (!entries[0].isIntersecting) return;
            observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 400px 0px' }
        ).observe(this.parentElement || this);
      }

      async load() {
        const root = ((window.Lalla && window.Lalla.routes && window.Lalla.routes.root) || '/').replace(/\/$/, '');
        const list = this.querySelector('[data-recent-list]');
        const results = await Promise.all(
          this.handles.map(async (handle) => {
            try {
              const response = await fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=${encodeURIComponent(this.dataset.sectionId)}`);
              if (!response.ok) return null;
              const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
              const template = doc.querySelector('template[data-recent-card]');
              return template ? template.content.cloneNode(true) : null;
            } catch (error) {
              return null;
            }
          })
        );
        const cards = results.filter(Boolean);
        if (!cards.length || !list) return;
        cards.forEach((card) => list.appendChild(card));
        this.hidden = false;
      }
    }
    customElements.define('recently-viewed', RecentlyViewed);
  }
})();
